import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

export default function Coupons() {
  const [merchants, setMerchants] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [selectedMerchant, setSelectedMerchant] = useState(null);

  const emptyForm = {
    merchant_id: '',
    merchant_name: '',
    deal: '',
    qualifiers: 'Not valid with any other offer. Must present this coupon with payment. Dine-in or carryout only. MUST PAY WITH FLATIRON TO USE.',
    expiry_date: '',
    expiry_date_actual: '',
    coupon_type: 'back_to_school',
    min_deposit_amount: '',
    estimated_value: '',
    active_fmp: true,
    active_athletics: false,
    active_website: false,
  };

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const [merchantsRes, couponsRes] = await Promise.all([
      supabase
        .from('merchants')
        .select('id, name, logo_url, address, city, state, zip, phone')
        .eq('active_fmp', true)
        .order('name', { ascending: true }),
      supabase
        .from('coupons')
        .select('*, merchants(name)')
        .order('created_at', { ascending: false }),
    ]);

    if (merchantsRes.data) setMerchants(merchantsRes.data);
    if (couponsRes.data) setCoupons(couponsRes.data);
    setLoading(false);
  };

  const handleMerchantChange = (merchantId) => {
    const merchant = merchants.find((m) => m.id === merchantId);
    setSelectedMerchant(merchant || null);
    setForm((prev) => ({
      ...prev,
      merchant_id: merchantId,
      merchant_name: merchant?.name || '',
    }));
  };

  const handleEdit = (coupon) => {
    setForm({
      merchant_id: coupon.merchant_id || '',
      merchant_name: coupon.merchant_name || '',
      deal: coupon.deal || '',
      qualifiers: coupon.qualifiers || '',
      expiry_date: coupon.expiry_date || '',
      expiry_date_actual: coupon.expiry_date_actual || '',
      coupon_type: coupon.coupon_type || 'back_to_school',
      min_deposit_amount: coupon.min_deposit_amount || '',
      estimated_value: coupon.estimated_value || '',
      active_fmp: coupon.active_fmp,
      active_athletics: coupon.active_athletics,
      active_website: coupon.active_website,
    });
    setEditingId(coupon.id);
    const merchant = merchants.find((m) => m.id === coupon.merchant_id);
    setSelectedMerchant(merchant || null);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditingId(null);
    setSelectedMerchant(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.merchant_id || !form.deal) {
      alert('Please select a merchant and enter a deal.');
      return;
    }

    setSaving(true);

    const payload = {
      merchant_id: form.merchant_id,
      merchant_name: form.merchant_name,
      deal: form.deal,
      qualifiers: form.qualifiers,
      expiry_date: form.expiry_date || null,
      expiry_date_actual: form.expiry_date_actual || null,
      coupon_type: form.coupon_type,
      min_deposit_amount: form.min_deposit_amount || null,
      estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
      active_fmp: form.active_fmp,
      active_athletics: form.active_athletics,
      active_website: form.active_website,
    };

    if (editingId) {
      const { error } = await supabase
        .from('coupons')
        .update(payload)
        .eq('id', editingId);

      if (error) {
        alert('Error updating coupon: ' + error.message);
        setSaving(false);
        return;
      }

      setSuccessMsg('Coupon updated!');
      setTimeout(() => setSuccessMsg(''), 4000);
      setForm(emptyForm);
      setEditingId(null);
      setSelectedMerchant(null);
      setShowForm(false);
      setSaving(false);
      fetchData();
      return;
    }

    // New coupon — insert and auto-assign
    const { data: newCoupon, error } = await supabase
      .from('coupons')
      .insert({ ...payload, redeemed: false })
      .select()
      .single();

    if (error) {
      alert('Error creating coupon: ' + error.message);
      setSaving(false);
      return;
    }

    // Auto-assign to all users
    const { data: allUsers } = await supabase.from('users').select('id');

    if (allUsers && allUsers.length > 0) {
      const assignments = allUsers.map((user) => ({
        user_id: user.id,
        coupon_id: newCoupon.id,
        assignment_reason: form.coupon_type,
        redeemed: false,
      }));
      await supabase.from('user_coupons').insert(assignments);
    }

    setSuccessMsg(`Coupon created and assigned to ${allUsers?.length || 0} users!`);
    setTimeout(() => setSuccessMsg(''), 4000);
    setForm(emptyForm);
    setSelectedMerchant(null);
    setShowForm(false);
    setSaving(false);
    fetchData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon? This will also remove it from all users.')) return;
    await supabase.from('user_coupons').delete().eq('coupon_id', id);
    await supabase.from('coupons').delete().eq('id', id);
    fetchData();
  };

  const handleToggle = async (coupon, field) => {
    await supabase.from('coupons').update({ [field]: !coupon[field] }).eq('id', coupon.id);
    fetchData();
  };

  if (loading) return (
    <div style={styles.loading}><p style={{ color: GOLD }}>Loading...</p></div>
  );

  return (
    <div style={styles.wrap}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>Coupons</h1>
        <button style={styles.topbarBtn} onClick={() => {
          if (showForm) {
            handleCancel();
          } else {
            setShowForm(true);
            setEditingId(null);
            setForm(emptyForm);
          }
        }}>
          {showForm ? '✕ Cancel' : '+ New Coupon'}
        </button>
      </div>

      <div style={styles.content}>

        {successMsg && <div style={styles.successBanner}>✅ {successMsg}</div>}

        {/* COUPON FORM */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>{editingId ? 'Edit Coupon' : 'Create New Coupon'}</h2>
            <form onSubmit={handleSubmit}>

              {/* Merchant dropdown */}
              <div style={styles.formRow}>
                <label style={styles.label}>MERCHANT</label>
                <select
                  style={styles.select}
                  value={form.merchant_id}
                  onChange={(e) => handleMerchantChange(e.target.value)}
                  required
                >
                  <option value="">Select a merchant...</option>
                  {merchants.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              {/* Merchant preview */}
              {selectedMerchant && (
                <div style={styles.merchantPreview}>
                  {selectedMerchant.logo_url && (
                    <img src={selectedMerchant.logo_url} alt={selectedMerchant.name} style={styles.merchantLogo} />
                  )}
                  <div>
                    <div style={styles.merchantPreviewName}>{selectedMerchant.name}</div>
                    {selectedMerchant.address && (
                      <div style={styles.merchantPreviewAddr}>
                        {selectedMerchant.address}, {selectedMerchant.city}, {selectedMerchant.state} {selectedMerchant.zip}
                      </div>
                    )}
                    {selectedMerchant.phone && (
                      <div style={styles.merchantPreviewPhone}>{selectedMerchant.phone}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Deal */}
              <div style={styles.formRow}>
                <label style={styles.label}>DEAL</label>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="e.g. Buy one entree, get one FREE"
                  value={form.deal}
                  onChange={(e) => setForm({ ...form, deal: e.target.value })}
                  required
                />
              </div>

              {/* Qualifiers */}
              <div style={styles.formRow}>
                <label style={styles.label}>QUALIFIERS</label>
                <textarea
                  style={styles.textarea}
                  value={form.qualifiers}
                  onChange={(e) => setForm({ ...form, qualifiers: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Expiry date */}
              <div style={styles.formRow}>
                <label style={styles.label}>EXPIRY DATE</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    style={{ ...styles.input, width: 'auto' }}
                    type="date"
                    onChange={(e) => {
                      if (e.target.value) {
                        const [year, month, day] = e.target.value.split('-');
                        const date = new Date(year, month - 1, day);
                        const formatted = date.toLocaleDateString('en-US', {
                          month: 'long', day: 'numeric', year: 'numeric',
                        });
                        setForm((prev) => ({
                          ...prev,
                          expiry_date: formatted,
                          expiry_date_actual: e.target.value,
                        }));
                      }
                    }}
                  />
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    type="text"
                    placeholder="e.g. Aug 31, 2026"
                    value={form.expiry_date}
                    onChange={(e) => setForm((prev) => ({ ...prev, expiry_date: e.target.value }))}
                  />
                </div>
                <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                  Pick from calendar or type manually
                </div>
              </div>

              {/* Coupon type */}
              <div style={styles.formRow}>
                <label style={styles.label}>COUPON TYPE</label>
                <select
                  style={styles.select}
                  value={form.coupon_type}
                  onChange={(e) => setForm({ ...form, coupon_type: e.target.value })}
                >
                  <option value="back_to_school">Back to School</option>
                  <option value="deposit_bonus">Deposit Bonus</option>
                </select>
              </div>

              {/* Min deposit — only for deposit bonus */}
              {form.coupon_type === 'deposit_bonus' && (
                <div style={styles.formRow}>
                  <label style={styles.label}>MINIMUM DEPOSIT AMOUNT ($)</label>
                  <input
                    style={styles.input}
                    type="number"
                    placeholder="e.g. 250"
                    value={form.min_deposit_amount}
                    onChange={(e) => setForm({ ...form, min_deposit_amount: e.target.value })}
                  />
                </div>
              )}

              {/* Estimated value */}
              <div style={styles.formRow}>
                <label style={styles.label}>ESTIMATED VALUE ($)</label>
                <input
                  style={styles.input}
                  type="number"
                  step="0.01"
                  placeholder="e.g. 15.00"
                  value={form.estimated_value}
                  onChange={(e) => setForm({ ...form, estimated_value: e.target.value })}
                />
                <div style={{ color: '#aaa', fontSize: '11px', marginTop: '4px' }}>
                  Used to calculate total coupon value shown on the website
                </div>
              </div>

              {/* Active toggles */}
              <div style={styles.formRow}>
                <label style={styles.label}>ACTIVE IN</label>
                <div style={styles.toggleRow}>
                  <label style={styles.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={form.active_fmp}
                      onChange={(e) => setForm({ ...form, active_fmp: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    FMP App
                  </label>
                  <label style={styles.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={form.active_athletics}
                      onChange={(e) => setForm({ ...form, active_athletics: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Gold Card App
                  </label>
                  <label style={styles.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={form.active_website}
                      onChange={(e) => setForm({ ...form, active_website: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Website
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button type="submit" style={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Coupon & Assign to All Users'}
                </button>
                <button type="button" style={styles.cancelBtn} onClick={handleCancel}>
                  Cancel
                </button>
              </div>

            </form>
          </div>
        )}

        {/* COUPONS TABLE */}
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>All Coupons ({coupons.length})</h2>
        </div>
        <div style={styles.tableWrap}>
          {coupons.length === 0 ? (
            <div style={styles.empty}>No coupons yet. Click "+ New Coupon" to create one.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Merchant', 'Deal', 'Type', 'Value', 'Expires', 'FMP', 'Gold Card', 'Website', 'Actions'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id}>
                    <td style={styles.td}>{c.merchant_name}</td>
                    <td style={styles.td}>{c.deal}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        background: c.coupon_type === 'back_to_school'
                          ? 'rgba(255,194,32,0.15)'
                          : c.coupon_type === 'deposit_bonus'
                          ? 'rgba(59,130,246,0.1)'
                          : '#f0f0f0',
                        color: c.coupon_type === 'back_to_school'
                          ? '#b8860b'
                          : c.coupon_type === 'deposit_bonus'
                          ? '#1d4ed8'
                          : '#666',
                      }}>
                        {c.coupon_type === 'back_to_school' ? 'Back to School'
                          : c.coupon_type === 'deposit_bonus' ? 'Deposit Bonus'
                          : c.coupon_type === 'value_load' ? 'Value Load'
                          : 'Standard'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {c.estimated_value ? `$${parseFloat(c.estimated_value).toFixed(2)}` : '—'}
                    </td>
                    <td style={styles.td}>{c.expiry_date || '—'}</td>
                    <td style={styles.td}>
                      <Toggle value={c.active_fmp} onChange={() => handleToggle(c, 'active_fmp')} />
                    </td>
                    <td style={styles.td}>
                      <Toggle value={c.active_athletics} onChange={() => handleToggle(c, 'active_athletics')} />
                    </td>
                    <td style={styles.td}>
                      <Toggle value={c.active_website} onChange={() => handleToggle(c, 'active_website')} />
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button style={styles.editBtn} onClick={() => handleEdit(c)}>Edit</button>
                        <button style={styles.deleteBtn} onClick={() => handleDelete(c.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div onClick={onChange} style={{ width: '36px', height: '20px', borderRadius: '10px', background: value ? GOLD : '#ddd', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', width: '14px', height: '14px', borderRadius: '50%', background: '#fff', top: '3px', left: value ? '19px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: { background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 },
  topbarTitle: { fontSize: '18px', fontWeight: '500', color: '#111', margin: 0 },
  topbarBtn: { background: GOLD, color: '#111', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  content: { padding: '24px', flex: 1 },
  successBanner: { background: '#f0fff4', border: '1px solid #86efac', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: '#166534', fontSize: '14px' },
  formCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '14px', padding: '24px', marginBottom: '28px' },
  formTitle: { fontSize: '16px', fontWeight: '500', color: '#111', marginBottom: '20px' },
  formRow: { marginBottom: '16px' },
  label: { display: 'block', color: '#888', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#111', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', color: '#111', fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#111', fontFamily: 'inherit', outline: 'none', background: '#fff', cursor: 'pointer', boxSizing: 'border-box' },
  merchantPreview: { display: 'flex', alignItems: 'center', gap: '14px', background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' },
  merchantLogo: { width: '48px', height: '48px', borderRadius: '10px', objectFit: 'contain', border: '1px solid #e0e0e0', background: '#fff' },
  merchantPreviewName: { fontSize: '14px', fontWeight: '500', color: '#111', marginBottom: '3px' },
  merchantPreviewAddr: { fontSize: '12px', color: '#888', marginBottom: '2px' },
  merchantPreviewPhone: { fontSize: '12px', color: GOLD },
  toggleRow: { display: 'flex', gap: '24px', flexWrap: 'wrap' },
  toggleLabel: { display: 'flex', alignItems: 'center', fontSize: '14px', color: '#444', cursor: 'pointer' },
  submitBtn: { background: GOLD, color: '#111', border: 'none', borderRadius: '10px', padding: '13px 24px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', flex: 1 },
  cancelBtn: { background: 'none', color: '#888', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '13px 24px', fontSize: '14px', cursor: 'pointer' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  sectionTitle: { fontSize: '15px', fontWeight: '500', color: '#111', margin: 0 },
  tableWrap: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { background: '#f9f9f9', color: '#888', fontSize: '11px', padding: '10px 16px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', fontWeight: '500' },
  td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', color: '#111', verticalAlign: 'middle' },
  badge: { display: 'inline-block', padding: '3px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '500' },
  editBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#444', cursor: 'pointer' },
  deleteBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#ff6b6b', cursor: 'pointer' },
  empty: { padding: '32px', textAlign: 'center', color: '#888', fontSize: '13px' },
};